"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { useIsMobile } from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";

/**
 * <ResponsiveDialog> — a Radix Dialog that renders as a centered modal on
 * desktop and as a bottom-anchored sheet on mobile (< 768px).
 *
 * Why this exists:
 *   Many existing modals (settings, invite, board property modals, cell
 *   editors, etc.) are fine on desktop but overflow the viewport on phones.
 *   Wrapping them in <ResponsiveDialog> instead of <Dialog> lets each callsite
 *   keep its current content/header/footer and automatically get a mobile-
 *   appropriate bottom-sheet presentation without depending on vaul or any
 *   other library — both paths are pure Radix Dialog under the hood, so focus
 *   management, escape handling, and accessibility behave identically.
 *
 * Phase 0 ships this wrapper definition only. No existing modal callsite is
 * migrated in this phase — that work lives in Phase 4 of the responsive plan.
 *
 * API: the exported component names mirror `@/components/tailwind/ui/dialog`
 * (Root, Trigger, Portal, Close, Overlay, Content, Header, Footer, Title,
 * Description). Callsites can drop in <ResponsiveDialog…> in place of
 * <Dialog…> with no other changes.
 *
 * The optional `forceLayout` prop on Content forces a specific presentation
 * regardless of viewport — handy for tests and Storybook only.
 */

const ResponsiveDialog = DialogPrimitive.Root;
const ResponsiveDialogTrigger = DialogPrimitive.Trigger;
const ResponsiveDialogPortal = DialogPrimitive.Portal;
const ResponsiveDialogClose = DialogPrimitive.Close;

const ResponsiveDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[300] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
ResponsiveDialogOverlay.displayName = "ResponsiveDialogOverlay";

type ResponsiveDialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Force a specific layout regardless of viewport. Defaults to `"auto"` which
   * means: bottom sheet on `< md`, centered modal on `≥ md`. Intended for tests
   * and visual-regression tooling — production callsites should leave it unset.
   */
  forceLayout?: "auto" | "modal" | "sheet";
};

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ResponsiveDialogContentProps
>(({ className, children, forceLayout = "auto", ...props }, ref) => {
  const isMobile = useIsMobile();
  const useSheet = forceLayout === "sheet" || (forceLayout === "auto" && isMobile);

  return (
    <ResponsiveDialogPortal>
      <ResponsiveDialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-[301] grid gap-4 border bg-background dark:bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          useSheet
            ? [
                "inset-x-0 bottom-0 max-h-[90dvh] overflow-y-auto rounded-t-lg p-4",
                "pb-[max(1rem,env(safe-area-inset-bottom))]",
                "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
              ].join(" ")
            : [
                "left-[50%] top-[50%] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] p-6 sm:rounded-lg",
                "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
                "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
              ].join(" "),
          className,
        )}
        {...props}
      >
        {/* Drag-handle affordance — visual only, not interactive. Signals
            "this is a sheet you can dismiss by swiping" on mobile. */}
        {useSheet ? (
          <div
            aria-hidden="true"
            className="mx-auto -mt-1 mb-1 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30"
          />
        ) : null}
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </ResponsiveDialogPortal>
  );
});
ResponsiveDialogContent.displayName = "ResponsiveDialogContent";

const ResponsiveDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
ResponsiveDialogHeader.displayName = "ResponsiveDialogHeader";

const ResponsiveDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
ResponsiveDialogFooter.displayName = "ResponsiveDialogFooter";

const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
ResponsiveDialogTitle.displayName = "ResponsiveDialogTitle";

const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
ResponsiveDialogDescription.displayName = "ResponsiveDialogDescription";

export {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogOverlay,
  ResponsiveDialogPortal,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
};
